import { useState } from 'react';
import { App, Alert, Button, Input, Modal, Tooltip } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import type { ArrangementDto, UnpublishImpactDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { listOriginOf, withRecordOrigin } from '@/lib/listOrigin';
import { UNPUBLISH_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [amended C3a §4] `Take back to draft` — EXTRACTED, BECAUSE §4 HID ITS ONLY DOOR.
 *
 *  ⚠️ THIS WAS A FEATURE REGRESSION AND IT ALMOST SHIPPED. [D4.4] built `PUBLISHED → DRAFT`
 *  deliberately, on a later client instruction that reversed [D4.3]'s "nothing in this system can
 *  un-publish", and the control existed ONLY on `pages/ArrangementDetail`. §4b named exactly two
 *  actions to hide — `Start a copy` and `Update duties` — and `SURFACES.arrangementRecordPage`
 *  took a third with them, silently: the endpoint, the impact read, the three tiers and the
 *  reason field were all still there with nothing left that could reach them.
 *
 *  ⚠️ ONE IMPLEMENTATION, TWO CALLERS — never a copy. `useArchiveArrangement` is this pass's own
 *  precedent one file over: a flow both the list and a record page perform is EXTRACTED, so the
 *  two cannot answer differently about what un-publishing costs. The hidden page keeps working
 *  through it, which is also what keeps `ArrangementDetail.test.tsx` alive as the hidden
 *  branch's coverage ([surfaces.ts]: a hidden branch is kept alive by a TEST, not a promise).
 *
 *  ⚠️ IT RENDERS NOTHING UNLESS THE RECORD CAN ACTUALLY BE TAKEN BACK. A draft has nothing to
 *  take back and an archived one is reached through Restore, so offering it on either would be a
 *  control that exists to be refused — §4a's rule, and the reason `canUnpublish` is computed here
 *  rather than by each caller.
 *
 *  ⚠️ A CONTROLLED `<Modal>`, NEVER `modal.confirm` — §8's rule for a required, validated input.
 *  `modal.confirm`'s `onOk` cannot hold the dialog open on an empty field, and its static form
 *  does not render under jsdom at all. It is therefore an overlay with a `census.spec.ts` entry,
 *  and moving it here is what keeps that entry at ONE rather than two.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const UnpublishAction = ({
  arrangementId,
  status,
  archived,
  canPublish,
  onDone,
}: {
  arrangementId: string;
  status: ArrangementDto['status'] | undefined;
  archived: boolean;
  canPublish: boolean;
  onDone: () => void;
}): React.ReactElement | null => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  /*
   * ⚠️ [TYPED-BACK] READ OFF THE RECORD PAGE THIS SITS ON, NEVER TAKEN AS A PROP — the hidden
   * `ArrangementDetail` is its second caller and stays unedited, and both pages carry `?from=` in the
   * router's own location (it arrives by navigation; neither writes the URL through nuqs).
   *
   * ⚠️ IT IS NOT CHECKED AGAINST THE ORDER'S TYPE HERE, which the record page does (`originForOrder`):
   * this component is handed no type, and the builder it opens checks the origin against the record it
   * loads before reading it — so a hand-made `?from=` carried this far still leads nowhere.
   */
  const origin = listOriginOf(useLocation().search);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  /* ⚠️ Gated on the OPEN, so a page that never offers it pays no request. */
  const impact = useQuery({
    queryKey: queryKeys.arrangements.unpublishImpact(arrangementId),
    queryFn: ({ signal }) => api.get<UnpublishImpactDto>(`/arrangements/${arrangementId}/unpublish-impact`, { signal }),
    enabled: open,
  });

  /*
   * ⚠️ THE RESET BELONGS TO THE COMMIT HANDLER **AND** THE DISMISSAL HANDLER, IN ONE FUNCTION —
   * [LIFECYCLE]'s rule. The modal is mounted while this component is, so `reason` outlives the
   * interaction and would otherwise be waiting, pre-filled, the next time. Never an effect:
   * `setState` in an effect body is a cascading render `eslint-plugin-react-hooks` refuses.
   */
  const close = (): void => {
    setOpen(false);
    setReason('');
  };

  const unpublish = useMutation({
    mutationFn: (why: string) => api.post<ArrangementDto>(`/arrangements/${arrangementId}/unpublish`, { reason: why }),
    onSuccess: () => {
      void message.success('Taken back to draft');
      close();
      onDone();
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
      /* Straight to the form, because changing it is the only reason to have done this. [TYPED-BACK]
         It is the record page's door (`?via=record` beside the origin), so the form goes back to the
         record it was taken back from — [D4.3] — and the record keeps the way back to the list. */
      void navigate(withRecordOrigin(`/arrangements/${arrangementId}/edit`, origin));
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const canUnpublish = !archived && (status === 'PUBLISHED' || status === 'ALLOCATION_IN_PROGRESS');
  if (!canUnpublish) return null;

  const blocked = impact.data?.tier === 'BLOCKED';

  return (
    <>
      {/*
        ⚠️ IT SITS BESIDE `Edit` RATHER THAN BESIDE THE ARCHIVE, because that is what it is FOR:
        a published order's dates and places are frozen, and this is the door to changing them.
        Putting it next to the destructive action would read as a lesser kind of deletion, which
        is exactly the confusion the archive-versus-un-publish split exists to prevent.
      */}
      <Tooltip title={canPublish ? UNPUBLISH_COPY.hint : READ_ONLY_TOOLTIP}>
        <Button
          disabled={!canPublish}
          data-testid="unpublish"
          onClick={() => {
            setOpen(true);
          }}
        >
          {UNPUBLISH_COPY.action}
        </Button>
      </Tooltip>

      <Modal
        open={open}
        title={UNPUBLISH_COPY.title}
        okText={UNPUBLISH_COPY.ok}
        okButtonProps={{
          danger: true,
          /* ⚠️ Blocked is refused HERE as well as on the server: an operator should meet the
             refusal before typing a reason, not after. */
          disabled: blocked || reason.trim() === '',
          'data-testid': 'unpublish-confirm',
        }}
        confirmLoading={unpublish.isPending}
        onCancel={close}
        onOk={() => {
          unpublish.mutate(reason.trim());
        }}
        data-testid="unpublish-modal"
      >
        <div className="flex flex-col gap-3 text-sm">
          {impact.isPending ? (
            <p className="text-muted-foreground">Checking what this will affect…</p>
          ) : impact.data ? (
            <>
              {blocked ? (
                <Alert
                  type="error"
                  showIcon
                  data-testid="unpublish-blocked"
                  message={impact.data.hasDutyEvent ? UNPUBLISH_COPY.blockedEvent : UNPUBLISH_COPY.blocked}
                />
              ) : (
                <>
                  <span>{UNPUBLISH_COPY.duties(impact.data.deploymentCount)}</span>
                  {impact.data.tier === 'DISCARDS_ASSIGNMENTS' ? (
                    <>
                      {/* ⚠️ TWO NUMBERS. "6 names" is the work lost; "5 officers" is the people
                          affected. One officer across six shifts is both.
                          ⚠️ [C6a] AND NEITHER WHEN THE ONLY NAMES ARE COMMANDERS. The tier reads
                          `DISCARDS_ASSIGNMENTS` for a division-named commander with no force name,
                          and "0 names … affecting 0 officers" above the command sentence would say
                          nothing is lost while something is. */}
                      {impact.data.assignmentCount > 0 || !impact.data.commandSeatsNamed ? (
                        <span data-testid="unpublish-discards">
                          {UNPUBLISH_COPY.discards(impact.data.assignmentCount, impact.data.affectedOfficerCount)}
                        </span>
                      ) : null}
                      {/* ⚠️ [C6a] A FLAG, NEVER A NUMBER — command enters no count (FR-5.2.6b). */}
                      {impact.data.commandSeatsNamed ? (
                        <span data-testid="unpublish-command">{UNPUBLISH_COPY.commandDiscarded}</span>
                      ) : null}
                      <span className="text-muted-foreground">
                        {UNPUBLISH_COPY.told(impact.data.affectedOrgUnitCount)}
                      </span>
                      {/* ⚠️ [C6a review] The asked desks are told and never counted — a flag, never a number. */}
                      {impact.data.commandSeatsAsked ? (
                        <span className="text-muted-foreground" data-testid="unpublish-command-told">
                          {UNPUBLISH_COPY.commandTold}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-muted-foreground">{UNPUBLISH_COPY.free}</span>
                  )}
                  {/*
                    ⚠️ [C2b §4g] EACH MEMBER'S OWN TWO NUMBERS, BELOW THIS ORDER'S OWN TWO. §4g
                    asks the confirmation to state BOTH counts, and the only way to state both is
                    to keep them apart — a sum would be a third figure matching neither the
                    parent's screen nor the corrigendum's.
                  */}
                  {impact.data.cascade.map((row) => (
                    <span key={row.id} data-testid="unpublish-cascade">
                      {UNPUBLISH_COPY.cascade(
                        row.paNumber ?? row.reference,
                        row.deploymentCount,
                        row.assignmentCount,
                        row.commandSeatsNamed,
                      )}
                    </span>
                  ))}
                </>
              )}
            </>
          ) : null}

          {!blocked && (
            <label className="flex flex-col gap-1">
              <span className="font-medium">{UNPUBLISH_COPY.reasonLabel}</span>
              <Input
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                data-testid="unpublish-reason"
                aria-label={UNPUBLISH_COPY.reasonLabel}
              />
              <span className="text-xs text-muted-foreground">{UNPUBLISH_COPY.reasonHint}</span>
            </label>
          )}
        </div>
      </Modal>
    </>
  );
};
